"use client";

import React, { useEffect, useState, useRef } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { fetchHomePageData, HomePageSectionData } from "@/api-service/homePage";
import { Product, Variant } from "@/packages/types/products";
import { useFastRouter } from "@/packages/hooks/useFastRouter";
import QuickViewModal from "../productdetail/QuickViewModal";

import { HEADER_V2_DESIGN_SYSTEM } from "../header/v2/designTokens";
import ProductCardV2 from "../product/ProductCardV2";
import { useResolvedCountryName } from "@/packages/hooks/useResolvedCountryName";
import { ProductGridSkeleton } from "../common/Skeletons";

interface TrendingProduct extends Product {
  variants?: Variant[];
}

interface SingleCategoryProductSectionProps {
  section: HomePageSectionData;
  countryName: string;
  onQuickView: (product: Product) => void;
}

const SingleCategoryProductSection: React.FC<SingleCategoryProductSectionProps> = ({
  section,
  countryName,
  onQuickView,
}) => {
  const categories = section.categories || [];
  const [activeCategory, setActiveCategory] = useState<number | null>(
    categories.length > 0 ? categories[0].id : null
  );
  const categoryListRef = useRef<HTMLDivElement>(null);
  const { fastPush } = useFastRouter();

  useEffect(() => {
    if (categories.length > 0 && (!activeCategory || !categories.some((c) => c.id === activeCategory))) {
      setActiveCategory(categories[0].id);
    }
  }, [categories, activeCategory]);

  const scrollCategories = (direction: "left" | "right") => {
    if (categoryListRef.current) {
      const scrollAmount = 200;
      const currentScroll = categoryListRef.current.scrollLeft;
      const newPosition =
        direction === "left"
          ? currentScroll - scrollAmount
          : currentScroll + scrollAmount;
      categoryListRef.current.scrollTo({
        left: newPosition,
        behavior: "smooth",
      });
    }
  };

  if (categories.length === 0) return null;

  const currentCategory = categories.find((c) => c.id === activeCategory);
  const activeProducts = (currentCategory?.products || []) as TrendingProduct[];

  return (
    <div className="w-full mb-8 md:mb-12 last:mb-0">
      {/* Title block */}
      <div className="mb-6 md:mb-8">
        <h2
          className={`font-semibold tracking-tight ${HEADER_V2_DESIGN_SYSTEM.typography.fontFamilyHeading} ${HEADER_V2_DESIGN_SYSTEM.typography.size20} ${HEADER_V2_DESIGN_SYSTEM.colors.primaryDarkText}`}
        >
          {section.title}
          {countryName ? ` in ${countryName}` : ""}
        </h2>
        {section.subtitle && (
          <p
            className={`mt-1.5 text-xs md:text-sm ${HEADER_V2_DESIGN_SYSTEM.colors.secondaryText} ${HEADER_V2_DESIGN_SYSTEM.typography.fontFamilySans}`}
          >
            {section.subtitle}
          </p>
        )}
      </div>

      {/* Mobile Layout: Row by Row with See All link */}
      <div className="block md:hidden space-y-8">
        {categories.map((category) => {
          const catProducts = (category.products || []) as TrendingProduct[];
          if (catProducts.length === 0) return null;

          return (
            <div key={category.id} className="space-y-4">
              <div className="flex justify-between items-baseline">
                <h3 className="font-bold text-slate-900 text-sm tracking-tight">
                  {category.name}
                </h3>
                <button
                  onClick={() => fastPush(`/collections/${category.slug}`)}
                  className="text-xs font-semibold text-slate-800 underline hover:text-[#D97706] transition-colors"
                >
                  See All
                </button>
              </div>

              <div className="grid grid-cols-2 gap-3">
                {catProducts.slice(0, 4).map((product) => (
                  <ProductCardV2
                    key={product.id}
                    product={product}
                    onQuickView={() => onQuickView(product)}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Desktop Layout: Tabbed Category Buttons + Product Grid */}
      <div className="hidden md:block space-y-6">
        <div className="flex items-center gap-2 border-b border-slate-200/80 pb-3">
          <button
            onClick={() => scrollCategories("left")}
            className="p-1 rounded-md hover:bg-slate-100 text-slate-600 transition"
            aria-label="Scroll category tabs left"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>

          <div
            ref={categoryListRef}
            className="flex items-center gap-2 overflow-x-auto scrollbar-none scroll-smooth flex-1"
          >
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setActiveCategory(category.id)}
                className={`px-4 py-2 rounded-lg text-sm font-semibold whitespace-nowrap transition-all duration-200 ${
                  activeCategory === category.id
                    ? "bg-[#0B132B] text-white shadow-xs"
                    : "bg-slate-100 text-slate-700 hover:bg-slate-200/80"
                }`}
              >
                {category.name}
              </button>
            ))}
          </div>

          <button
            onClick={() => scrollCategories("right")}
            className="p-1 rounded-md hover:bg-slate-100 text-slate-600 transition"
            aria-label="Scroll category tabs right"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>

        {/* Desktop Product Grid */}
        {activeProducts.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-6">
            {activeProducts.map((product) => (
              <ProductCardV2
                key={product.id}
                product={product}
                onQuickView={() => onQuickView(product)}
              />
            ))}
          </div>
        ) : (
          <div className="py-12 text-center text-slate-500 text-sm">
            No products found in this category.
          </div>
        )}
      </div>
    </div>
  );
};

interface TrendingCategoriesProps {
  section?: HomePageSectionData;
}

const TrendingCategoriesV2: React.FC<TrendingCategoriesProps> = ({ section: passedSection }) => {
  const countryName = useResolvedCountryName();
  const [sections, setSections] = useState<HomePageSectionData[]>([]);
  const [loading, setLoading] = useState(!passedSection);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [isQuickViewOpen, setIsQuickViewOpen] = useState(false);

  useEffect(() => {
    if (passedSection) {
      setSections([passedSection]);
      setLoading(false);
      return;
    }

    let isMounted = true;
    const loadHomePageData = async () => {
      try {
        setLoading(true);
        const res = await fetchHomePageData();
        if (isMounted) {
          const allSections = res?.data?.sections || [];
          const catProdSecs = allSections.filter(
            (sec) => sec.sectionType?.toUpperCase() === "CATEGORY_PRODUCTS"
          );
          setSections(catProdSecs);

        }
      } catch (error: unknown) {
        console.error("Error fetching homepage category products data:", error);
        if (isMounted) {
          toast.error("Error fetching category products sections");
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadHomePageData();
    return () => {
      isMounted = false;
    };
  }, [passedSection]);

  const handleQuickView = (product: Product) => {
    setSelectedProduct(product);
    setIsQuickViewOpen(true);
  };

  if (passedSection) {
    if (!passedSection.categories || passedSection.categories.length === 0) {
      return null;
    }
    return (
      <section
        className={`w-full max-w-7xl mx-auto my-8 md:my-12 ${HEADER_V2_DESIGN_SYSTEM.spacing.containerPadding}`}
      >
        <SingleCategoryProductSection
          key={passedSection.id}
          section={passedSection}
          countryName={countryName}
          onQuickView={handleQuickView}
        />
        {selectedProduct && (
          <QuickViewModal
            isOpen={isQuickViewOpen}
            onClose={() => setIsQuickViewOpen(false)}
            slug={selectedProduct.slug}
            product={selectedProduct}
          />
        )}

      </section>
    );
  }

  if (!loading && sections.length === 0) {
    return null;
  }

  return (
    <section
      className={`w-full max-w-7xl mx-auto my-8 md:my-12 ${HEADER_V2_DESIGN_SYSTEM.spacing.containerPadding}`}
    >
      {loading ? (
        <div className="w-full space-y-6">
          <div className="hidden md:flex gap-2 pb-2">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="h-9 w-28 bg-slate-200 rounded-lg animate-pulse" />
            ))}
          </div>
          <ProductGridSkeleton count={5} />
        </div>
      ) : (
        sections.map((sec) => (
          <SingleCategoryProductSection
            key={sec.id}
            section={sec}
            countryName={countryName}
            onQuickView={handleQuickView}
          />
        ))
      )}

      {/* Quick View Modal */}
      {selectedProduct && (
        <QuickViewModal
          isOpen={isQuickViewOpen}
          onClose={() => setIsQuickViewOpen(false)}
          product={selectedProduct}
        />
      )}
    </section>
  );
};

export default TrendingCategoriesV2;

